/** @jsx jsx */
import React from "react";
import { css, jsx } from "@emotion/core";
import { colors } from "../colors";
import { ListItem } from "../ListItem";

interface Props extends React.ComponentProps<typeof ListItem> {
  /**
   * Add a number as a right adornment. Usually used to express a count
   */
  count?: React.ReactNode;
  /**
   * the span wrapping children makes some layouts impossible, this is an escape hatch until we can fix all usages and make sure removing the span doesn't break them
   */
  dontWrapHeadingInSpan?: boolean;
}

/**
 * Heading intended to be used in a list. Can also be used to deliniate between
 * sections in a single list.
 *
 * Composed with and accepts all props of `ListItem`
 *
 * @deprecated use Orbit's `ListLabel` instead.
 * import { ListLabel } from '@apollo/orbit'
 */
export const ListHeading = ({
  children,
  count,
  dontWrapHeadingInSpan = false,
  ref,
  ...props
}: React.PropsWithChildren<Props> & {
  ref?: React.Ref<HTMLHeadingElement>;
}) => {
  const { onClick } = props;

  // Stop click events so we don't try to close the list when clicking something
  // non-interactive
  const handleClick = React.useCallback<NonNullable<typeof props.onClick>>(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      onClick?.(event);
    },
    [onClick],
  );

  return (
    <ListItem {...props} interactive={false} onClick={handleClick}>
      <h2
        css={css({
          display: "flex",
          fontSize: "inherit",
          fontWeight: 600,
          margin: 0,
          marginBottom: 2,
          marginTop: 3,
          textTransform: "uppercase",
          width: "100%",
        })}
        ref={ref}
      >
        {dontWrapHeadingInSpan ? (
          children
        ) : (
          <span css={css({ flex: "1" })}>{children}</span>
        )}
        {count && (
          <span
            css={css({
              color: colors.grey.light,
              flex: "none",
              fontWeight: "normal",
            })}
          >
            {count}
          </span>
        )}
      </h2>
    </ListItem>
  );
};
